/**
 * Grants/revokes Play on android per-instance streams so named-player input
 * passes Playjector's RouteInstanceInput Play-role gate.
 *
 * Player input messages use header.user.id = peerId (see marketing inputEvents),
 * so attach/detach address that same id. Tracks attachments so leave can detach
 * without metadata on onPlayerLeft.
 */

import { generators, type PlaycastMessage, type PlaycastMessageTarget } from '@playcastdotio/Messaging';

export type StreamParticipantManagerDeps = {
  send: (message: PlaycastMessage<PlaycastMessageTarget>) => void;
  getActiveAndroidStreamId: () => string | undefined;
};

type Attachment = {
  streamId: string;
  gameInstanceIndex: number;
};

export class StreamParticipantManager {
  private readonly attachments = new Map<string, Attachment>();

  constructor(private readonly deps: StreamParticipantManagerDeps) {}

  /**
   * Attach Play for a player matched to a game instance. No-op when there is no
   * active android stream or the session lacks a gameInstanceIndex.
   */
  public attachPlayer(
    peerId: string,
    gameInstanceIndex: number | undefined,
  ): void {
    if (!peerId || gameInstanceIndex === undefined || gameInstanceIndex < 0) return;

    const streamId = this.deps.getActiveAndroidStreamId();
    if (!streamId) return;

    this.deps.send(
      generators.stream.attachParticipant(streamId, peerId, ['play'], gameInstanceIndex),
    );
    this.attachments.set(peerId, { streamId, gameInstanceIndex });
  }

  /** Detach a previously attached player (best-effort; clears local tracking either way). */
  public detachPlayer(peerId: string): void {
    const attachment = this.attachments.get(peerId);
    if (!attachment) return;

    this.deps.send(
      generators.stream.detachParticipant(
        attachment.streamId,
        peerId,
        attachment.gameInstanceIndex,
      ),
    );
    this.attachments.delete(peerId);
  }
}
